/** Canonical Product return-target parser and consume-once storage authority. */

import { classifyBeforeAuth } from '../next/app/routes/productRouteProfile';

export const BETA_LANDING_PATH = '/practice';

/**
 * Where Auth0 returns after a redirect sign-in. Must be a PRODUCT-OWNED route,
 * never the bare origin: `legacy-monolith/routeOwnership.ts` hands `/` to the
 * Marketing tree, which mounts no Auth0Provider, so a callback there lands where
 * `handleRedirectCallback()` never runs -- the `?code=` is silently discarded and
 * the marketing home page renders, with no error and no session. `/login` is not
 * in that file's PUBLIC_EXACT_PATHS, so it renders the Product tree and
 * LoginPage's syncAuth effect completes the exchange.
 *
 * Adding this path to PUBLIC_EXACT_PATHS reintroduces the silent sign-in
 * failure; `LegacyMonolithApp.test.tsx` guards the pairing.
 */
export const AUTH0_CALLBACK_PATH = '/login';
export const SAFE_RETURN_STORAGE_KEY = 'pp_product_safe_return_v1';
export const SAFE_RETURN_TTL_MS = 30 * 60 * 1000;

/** `/family` is a reviewed, guardian-owned return target in every build. */
const FAMILY_SAFE_RETURN = true;

// v1-archive-p4a (2026-08-20): '/dev', the CB_PREVIEW regex, and 'admin'
// inside PROGRAM_CHILD are now retired routes with no v1 component behind
// them (deleted in P4a). Deliberately NOT cleaned up here -- P3 already
// left every other retired program child (dashboard, test, stats,
// history, ...) in this allowlist, and the allowlist is inert with
// respect to honesty: a stored return to a retired path is honored,
// navigates there, and root-redirects to /practice -- the identical
// endpoint as every other stale entry. Removing only the P4a entries while
// the P3 entries remain would make this file incoherent. Full cleanup is
// a P5 row (docs/tech/frontend/v1-archive-inventory.md's P5-deferrals
// note; execution-proposal.md §1.7).
const SIMPLE_FAMILIES = ['/practice', '/insights', '/next/app', '/vocab-mastery', '/cheatsheet'] as const;
const SIMPLE_EXACT = new Set([
  '/today', '/map-reveal', '/profile', '/settings', '/coach',
  '/celebration', '/onboarding', '/goal-setup', '/programs', '/dev',
]);
const PROGRAM_CHILD = /^(?:dashboard|test|tests\/(?:synonyms|math|sentence-completion|reading(?:\/[^/]+)?|session|practice|[^/]+\/analytics)|tools\/flashcards|mistakes|single-word|paired-word|clues|reading(?:\/[^/]+)?|exams\/stats|stats|history|admin|study\/(?:synonyms|clues|test)|writing|readiness\/[^/]+|curriculum|analysis\/verbal)$/;
const SAT_KG_CHILD = /^kg(?:\/(?:plan|practice|progress|explore))?$/;
const CB_PREVIEW = /^\/cb-preview(?:\/[^/]+(?:\/(?:q\/[^/]+|[^/]+\/q\/[^/]+))?)?$/;
// A raw control-character regex charset trips `no-control-regex` even when
// written via `\u00xx` escapes (the rule inspects the pattern's resulting
// code points, not the source spelling). Check char codes directly instead --
// identical matching semantics, no control-character regex range.
function hasControlOrBackslash(value: string): boolean {
  for (let i = 0; i < value.length; i += 1) {
    const code = value.charCodeAt(i);
    if (code <= 0x1f || code === 0x7f || value[i] === '\\') return true;
  }
  return false;
}

/**
 * Decision L2 (docs/agent/comms/follow_ups/learn/2026-09-24-learn-capture-web-
 * companion-decisions.md): the Pinpoint Learn web companion owns every
 * `/learn/<subpath>` in Product. Exact `/learn` and `/learn/capture` stay
 * Marketing-owned in the single-origin shell
 * (legacy-monolith/src/routeOwnership.ts), so neither is ever returned into
 * Product. `/learn/` with no subpath names no Learn page and is rejected.
 *
 * Decision O-C1a: the canonical Learn home is `/learn/library`. A captured
 * return of exact `/learn` (the app-host URL-only entry, L11) normalizes to
 * it, so the student still lands on the Learn home after sign-in while the
 * parser never answers exact `/learn` on any host. `/learn/capture` has no
 * Product page and falls back to BETA_LANDING_PATH.
 * `scripts/audit/check_product_route_ownership.py` reads these literals.
 */
export const LEARN_HOME_RETURN_PATH = '/learn/library';
const LEARN_ENTRY_PATH = '/learn';
const LEARN_PREFIX = '/learn/';
const LEARN_MARKETING_EXACT = new Set(['/learn/capture']);

function normalizeLearnEntry(pathname: string): string {
  return pathname === LEARN_ENTRY_PATH ? LEARN_HOME_RETURN_PATH : pathname;
}

function isLearnProductSubpath(pathname: string): boolean {
  return pathname.startsWith(LEARN_PREFIX)
    && pathname.length > LEARN_PREFIX.length
    && !LEARN_MARKETING_EXACT.has(pathname);
}

/**
 * True for the Product Learn surface: the app-host `/learn` entry (redirected to
 * the Learn home) and every Product `/learn/*` subpath. Learn has its own
 * server-side one-account gate, so account-type fences (guardian → /family,
 * untyped → /choose-role) must not intercept it (decision O-C1b).
 */
export function isLearnSurfacePath(pathname: string): boolean {
  return pathname === LEARN_ENTRY_PATH || isLearnProductSubpath(pathname);
}

function isBoundaryFamily(pathname: string, family: string): boolean {
  return pathname === family || pathname.startsWith(`${family}/`);
}

function isReviewedProductPath(pathname: string): boolean {
  if (SIMPLE_EXACT.has(pathname)) return true;
  if (SIMPLE_FAMILIES.some((family) => isBoundaryFamily(pathname, family))) return true;
  if (FAMILY_SAFE_RETURN && isBoundaryFamily(pathname, '/family')) return true;
  if (CB_PREVIEW.test(pathname)) return true;
  if (isLearnProductSubpath(pathname)) return true;
  const program = pathname.match(/^\/(sat|isee)(?:\/(.*))?$/);
  if (!program) return false;
  const [, name, child = ''] = program;
  if (!child) return true;
  if (name === 'sat' && SAT_KG_CHILD.test(child)) return true;
  return PROGRAM_CHILD.test(child);
}

/** Parse an untrusted return target without ever accepting an external origin. */
export function parseSafeReturn(candidate: unknown): string | null {
  if (typeof candidate !== 'string' || !candidate.startsWith('/') || candidate.startsWith('//')) return null;
  if (hasControlOrBackslash(candidate)) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(candidate);
  } catch {
    return null;
  }
  if (hasControlOrBackslash(decoded) || decoded.startsWith('//')) return null;
  let parsed: URL;
  try {
    parsed = new URL(candidate, 'https://product.invalid');
  } catch {
    return null;
  }
  if (parsed.origin !== 'https://product.invalid') return null;
  const pathname = normalizeLearnEntry(parsed.pathname);
  if (!isReviewedProductPath(pathname)) return null;
  return `${pathname}${parsed.search}${parsed.hash}`;
}

function candidatePathEnd(value: string): number {
  const query = value.indexOf('?');
  const hash = value.indexOf('#');
  if (query === -1) return hash === -1 ? value.length : hash;
  return hash === -1 ? query : Math.min(query, hash);
}

/**
 * Candidate returns name only mounted canonical product pages. The route
 * profile is the same generated-manifest authority used before authentication,
 * so auth/setup/status, Marketing, retired, fallback, and unknown URLs cannot
 * become post-auth destinations.
 */
export function parseCandidateSafeReturn(candidate: unknown): string | null {
  if (typeof candidate !== 'string' || !candidate.startsWith('/') || candidate.startsWith('//')) return null;
  if (hasControlOrBackslash(candidate)) return null;
  let decoded: string;
  try {
    decoded = decodeURIComponent(candidate);
  } catch {
    return null;
  }
  if (hasControlOrBackslash(decoded) || decoded.startsWith('//')) return null;
  let parsed: URL;
  try {
    parsed = new URL(candidate, 'https://product.invalid');
  } catch {
    return null;
  }
  if (parsed.origin !== 'https://product.invalid') return null;
  const rawPathname = candidate.slice(0, candidatePathEnd(candidate));
  if (parsed.pathname !== rawPathname) return null;
  const decision = classifyBeforeAuth('www-candidate', parsed.pathname);
  if (decision.kind !== 'product') return null;
  if (!/^candidate-(?:sat|family|learn)-/.test(decision.routeId)) return null;
  return `${parsed.pathname}${parsed.search}${parsed.hash}`;
}

export function isSafeInternalNext(candidate: unknown): boolean {
  return parseSafeReturn(candidate) !== null;
}

export function resolvePostAuthDestination(candidate: unknown): string {
  return parseSafeReturn(candidate) ?? BETA_LANDING_PATH;
}

type StoredSafeReturn = { path: string; createdAt: number };

export function storeSafeReturn(candidate: unknown, now = Date.now()): string | null {
  const path = parseSafeReturn(candidate);
  try {
    if (!path) {
      window.sessionStorage.removeItem(SAFE_RETURN_STORAGE_KEY);
      return null;
    }
    const record: StoredSafeReturn = { path, createdAt: now };
    window.sessionStorage.setItem(SAFE_RETURN_STORAGE_KEY, JSON.stringify(record));
    return path;
  } catch {
    return null;
  }
}

export function storeCandidateSafeReturn(candidate: unknown, now = Date.now()): string | null {
  const path = parseCandidateSafeReturn(candidate);
  try {
    if (!path) {
      window.sessionStorage.removeItem(SAFE_RETURN_STORAGE_KEY);
      return null;
    }
    const record: StoredSafeReturn = { path, createdAt: now };
    window.sessionStorage.setItem(SAFE_RETURN_STORAGE_KEY, JSON.stringify(record));
    return path;
  } catch {
    return null;
  }
}

/** Remove before validating so malformed, expired, or replayed values are one-shot. */
function consumeStoredSafeReturn(now: number): string | null {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(SAFE_RETURN_STORAGE_KEY);
    window.sessionStorage.removeItem(SAFE_RETURN_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const record = JSON.parse(raw) as Partial<StoredSafeReturn>;
    if (typeof record.createdAt !== 'number' || now < record.createdAt || now - record.createdAt > SAFE_RETURN_TTL_MS) {
      return null;
    }
    return parseSafeReturn(record.path);
  } catch {
    return null;
  }
}

function consumeStoredCandidateSafeReturn(now: number): string | null {
  let raw: string | null;
  try {
    raw = window.sessionStorage.getItem(SAFE_RETURN_STORAGE_KEY);
    window.sessionStorage.removeItem(SAFE_RETURN_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const record = JSON.parse(raw) as Partial<StoredSafeReturn>;
    if (typeof record.createdAt !== 'number' || now < record.createdAt || now - record.createdAt > SAFE_RETURN_TTL_MS) {
      return null;
    }
    return parseCandidateSafeReturn(record.path);
  } catch {
    return null;
  }
}

export function consumeSafeReturn(now = Date.now()): string {
  return consumeStoredSafeReturn(now) ?? BETA_LANDING_PATH;
}

/**
 * Auth0 appState corroborates, but never replaces, the Product-origin one-shot
 * record. Consume the record before comparison so every callback outcome is
 * replay-safe, including missing, malformed, expired, and mismatched state.
 */
export function consumeAuth0SafeReturn(candidate: unknown, now = Date.now()): string {
  const expected = parseSafeReturn(candidate);
  const stored = consumeStoredSafeReturn(now);
  return expected && stored === expected ? stored : BETA_LANDING_PATH;
}

/** Candidate callbacks never guess an area; the verified profile resolves it. */
export function consumeAuth0CandidateSafeReturn(candidate: unknown, now = Date.now()): string | null {
  const expected = parseCandidateSafeReturn(candidate);
  const stored = consumeStoredCandidateSafeReturn(now);
  return expected && stored === expected ? stored : null;
}
